import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { AttributeStatus, EdgeEstimationEntry, EdgeSizeChipData } from '../../components/Prediction/types';
import { EdgeSizesPredictionDTO, PredictionsService } from '../generated';
import { BoundingBox } from '../../components/Documentation/ImageCard';
import { createElement } from 'react';
import StraightenIcon from '@mui/icons-material/Straighten';

export const edgeEstimationKeys = {
  base: () => ['edgeEstimation'] as const,
  one: ({ jobsiteId, imageId, config }: EdgeEstimationOptionProps) => [
    ...edgeEstimationKeys.base(),
    jobsiteId,
    imageId,
    config,
  ],
};

export interface EdgeEstimationOptionProps {
  jobsiteId: string | undefined;
  imageId: string | undefined;
  config?: EdgeEstimationConfig;
}

export function edgeEstimationOptions({
  jobsiteId,
  imageId,
  enabled = true,
  config,
}: EdgeEstimationOptionProps & { enabled: boolean }) {
  const transformFn = (resp: EdgeSizesPredictionDTO) => transformEdgeEstimation(resp?.output, config);

  return queryOptions<EdgeSizeChipData[], ApiError>({
    queryKey: edgeEstimationKeys.one({ jobsiteId, imageId, config }),
    queryFn: async () => {
      if (!jobsiteId || !imageId) {
        throw new Error('jobsiteId and imageId are required');
      }

      const response = await PredictionsService.predictionControllerGetEdgeSizesPredictionByImageId({
        path: { jobsiteId, imageId },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!imageId && enabled,
  });
}

export function useEdgeEstimation(props: EdgeEstimationOptionProps & { enabled: boolean }) {
  return useQuery(edgeEstimationOptions(props));
}

export const BOUNDING_BOX_LIMIT = 5;

export interface EdgeEstimationConfig {
  sizeThreshold: number; // meters
  method: 'diagonal' | 'edge';
  k: number;
  boundingBoxLimit: number;
}

export function transformEdgeEstimation(
  output: EdgeSizesPredictionDTO['output'] | EdgeEstimationEntry[] | undefined,
  config?: EdgeEstimationConfig
): EdgeSizeChipData[] {
  const edgeOutput = (output ?? []) as EdgeEstimationEntry[];
  if (edgeOutput.length === 0) return [];

  if (!config) {
    config = {
      sizeThreshold: 0.6,
      method: 'edge',
      k: 1,
      boundingBoxLimit: BOUNDING_BOX_LIMIT,
    };
  }

  const { sizeThreshold, method, k, boundingBoxLimit } = config;
  const sortedEdges = getSortedEdges(edgeOutput, method);
  const topK = sortedEdges.slice(0, k);
  const status = calculateEdgeStatus(topK, sizeThreshold);

  let edgesToDisplay;

  let valueText: string;
  let tooltipText: string;
  let tooltipTextParam = String(sizeThreshold * 100);
  let iconColor: 'warning' | 'inherit';
  switch (status) {
    case 'OK':
      valueText = `<${sizeThreshold * 100}cm`;
      tooltipText = 'attributes.edge-sizes.ok';
      iconColor = 'inherit';
      edgesToDisplay = sortedEdges;
      break;
    case 'CRITICAL':
      valueText = `>=${sizeThreshold * 100}cm`;
      tooltipText = 'attributes.edge-sizes.critical';
      iconColor = 'warning';
      edgesToDisplay = sortedEdges.filter(([width, height]) => width >= sizeThreshold || height >= sizeThreshold);
  }
  const activeBoundingBoxLimit = edgesToDisplay.length > boundingBoxLimit ? boundingBoxLimit : edgesToDisplay.length;

  return [
    {
      id: 'edge-estimation',
      name: 'attributes.edge-sizes.displayName',
      value: valueText,
      tooltipText: tooltipText,
      tooltipTextParam: tooltipTextParam,
      icon: createElement(StraightenIcon, { sx: { flexShrink: 0, marginLeft: 0.5 }, color: iconColor }),
      status: status,
      probability: undefined,
      clickable: true,
      edges: sortedEdges,
      objectsAboveThreshold: edgesToDisplay.length,
      boundingBoxes: getBoundingBoxes(edgesToDisplay, activeBoundingBoxLimit),
    },
  ];
}

export function calculateEdgeStatus(topK: EdgeEstimationEntry[], sizeThreshold: number = 0.6): AttributeStatus {
  const allAboveThreshold = topK.every(([width, height]) => width > sizeThreshold || height > sizeThreshold);
  const allBelowThreshold = topK.every(([width, height]) => width < sizeThreshold || height > sizeThreshold);

  if (allAboveThreshold) {
    return 'CRITICAL';
  }
  if (allBelowThreshold) {
    return 'OK';
  }
  // If neither all above nor all below, it implies a mix or exactly on threshold, which we consider OK.
  return 'OK';
}

function getSortedEdges(allSizes: EdgeEstimationEntry[], method: 'diagonal' | 'edge' = 'edge'): EdgeEstimationEntry[] {
  const sort = method === 'edge' ? sortByEdge : sortByDiagonal;
  return sort([...allSizes]);
}

function sortByEdge(allSizes: EdgeEstimationEntry[]): EdgeEstimationEntry[] {
  return allSizes.sort((a, b) => {
    const maxA = Math.max(a[0], a[1]);
    const maxB = Math.max(b[0], b[1]);
    return maxB - maxA;
  });
}

function sortByDiagonal(allSizes: EdgeEstimationEntry[]): EdgeEstimationEntry[] {
  return allSizes.sort((a, b) => {
    const maxA = Math.sqrt(a[0] ** 2 + a[1] ** 2);
    const maxB = Math.sqrt(b[0] ** 2 + b[1] ** 2);
    return maxB - maxA;
  });
}

function getBoundingBoxes(sortedEdges: EdgeEstimationEntry[], limit: number): BoundingBox[] {
  if (sortedEdges.length === 0) return [];

  return sortedEdges
    .map((edge) => {
      const [width, height, coordinates] = edge;
      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 4) return null;
      const isWidthLargest = width >= height;

      const [relX1, relY1, relX2, relY2] = coordinates;

      const boundingBox: BoundingBox = {
        rel_x1: Math.min(relX1, relX2),
        rel_y1: Math.min(relY1, relY2),
        rel_x2: Math.max(relX1, relX2),
        rel_y2: Math.max(relY1, relY2),
        color: width > 1 || height > 1 ? 'danger' : 'warning',
        horizontalLabel: isWidthLargest ? formatEdgeValue(width) : undefined,
        verticalLabel: !isWidthLargest ? formatEdgeValue(height) : undefined,
      };

      return boundingBox;
    })
    .filter((boundingBox): boundingBox is BoundingBox => boundingBox !== null)
    .slice(0, limit);
}

function formatEdgeValue(value: number): string {
  return `${value.toFixed(2)}`;
}
